import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function timeLabel(iso) {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export function ChatBox({ messages, onSend, isMine, loading, sending, emptyText, placeholder, onOpen }) {
  const [text, setText] = useState("");
  const listRef = useRef(null);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);
  const submit = async () => { const t = text.trim(); if (!t) return; setText(""); await onSend(t); };
  return (
    <div className="flex flex-col h-[50vh]" data-testid="chat-box">
      <div ref={listRef} className="flex-1 overflow-y-auto thin-scroll space-y-2 p-1">
        {loading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>
          : messages.length === 0 ? <div className="text-sm text-slate-400 text-center py-8">{emptyText || "No messages yet."}</div>
          : messages.map((m) => {
              const mine = isMine(m);
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`} data-testid={`chat-msg-${m.id}`}>
                    {!mine && <div className="text-[11px] font-medium opacity-70 mb-0.5">{m.sender_name}{m.sender_type === "patient" ? " · patient" : ""}</div>}
                    <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-0.5 ${mine ? "text-blue-100" : "text-slate-400"}`}>{timeLabel(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <Input value={text} onChange={(e) => setText(e.target.value)} onFocus={() => onOpen && onOpen()} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())} placeholder={placeholder || "Type a message"} data-testid="chat-input" />
        <Button size="icon" onClick={submit} disabled={sending || !text.trim()} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function StaffPatientChat({ patientId }) {
  const { auth } = useAuth();
  const meId = auth?.user?.id;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadMsgs = useCallback(async () => {
    try {
      const r = await api.get(`/opd/patients/${patientId}/messages`);
      setMessages(r.data || []);
      api.post(`/opd/patients/${patientId}/messages/read`).catch(() => {});
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => {
    setLoading(true); loadMsgs();
    const iv = setInterval(loadMsgs, 5000);
    return () => clearInterval(iv);
  }, [loadMsgs]);

  const send = async (text) => {
    setSending(true);
    try { await api.post(`/opd/patients/${patientId}/messages`, { text }); await loadMsgs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setSending(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">Shared conversation — the patient, treating doctor and invited care-team staff can all see and post here.</p>
      <ChatBox messages={messages} onSend={send} loading={loading} sending={sending}
        isMine={(m) => m.sender_type === "staff" && String(m.sender_id) === String(meId)}
        emptyText="No messages yet. Start the conversation." />
    </div>
  );
}

export function CareTeamPanel({ patientId }) {
  const [data, setData] = useState(null);
  const [dir, setDir] = useState([]);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/opd/patients/${patientId}/care-team`).then((r) => setData(r.data)).catch(() => setData({ members: [], can_manage: false })), [patientId]);
  useEffect(() => { load(); api.get("/opd/staff-directory").then((r) => setDir(r.data || [])).catch(() => {}); }, [load]);
  const add = async () => {
    if (!sel) return; setBusy(true);
    try { await api.post(`/opd/patients/${patientId}/care-team`, { user_id: sel }); setSel(""); toast.success("Added to care team"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api.delete(`/opd/patients/${patientId}/care-team/${id}`); load(); } catch { toast.error("Failed to remove"); } };
  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>;
  const memberIds = new Set(data.members.map((m) => m.user_id));
  const options = dir.filter((u) => !memberIds.has(u.id));
  return (
    <div className="space-y-4" data-testid="care-team-panel">
      <p className="text-sm text-slate-500">Invited staff can view this patient's records and take part in the chat, even if their role normally can't see patients.</p>
      {data.can_manage && (
        <div className="flex items-center gap-2">
          <select className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="care-team-select">
            <option value="">Invite a staff member…</option>
            {options.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
          </select>
          <Button size="sm" onClick={add} disabled={busy || !sel} className="bg-blue-700 hover:bg-blue-800" data-testid="care-team-add"><UserPlus className="h-4 w-4 mr-1.5" /> Invite</Button>
        </div>
      )}
      <div className="space-y-2">
        {data.members.length === 0 && <div className="text-sm text-slate-400">No one invited yet.</div>}
        {data.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between border border-slate-100 rounded-md px-3 py-2.5" data-testid={`care-team-member-${m.id}`}>
            <div>
              <div className="text-sm font-medium text-slate-800">{m.user_name}</div>
              <div className="text-[11px] text-slate-400">{m.user_email} · added by {m.added_by}</div>
            </div>
            {data.can_manage && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => remove(m.id)} data-testid={`care-team-remove-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
